import React, { useMemo, useState, useEffect, useRef } from "react";
import { Bar } from "@visx/shape";
import { Group } from "@visx/group";
import { AxisLeft } from "@visx/axis";
import { scaleLinear, scaleBand, scaleOrdinal } from "@visx/scale";
import { Text } from "@visx/text";
import { useChartDimensions } from "../../../../hooks/useChartDimensions";
import apiClient from "../../../../utility/axios";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const DemographicStackedBarChart = ({
  data,
  width = 450,
  height = 400,
  margin = { top: 20, right: 5, bottom: 20, left: 75 },
  colors = [
    "#157787", // Positive - Dark Teal
    "#bf5279", // Negative - Pink
    "#808080", // Neutral - Gray
  ],
  className = "",
  showPercentages = true,
  description,
  loading = false,
  error = null,
  demographic = null, // Add demographic prop for custom labeling
  showLegend = true, // Add showLegend prop to control legend visibility
}) => {
  // Use custom hook for responsive chart dimensions
  const [containerRef, dimensions] = useChartDimensions({
    width,
    height,
    marginTop: margin.top,
    marginRight: margin.right,
    marginBottom: margin.bottom,
    marginLeft: margin.left,
  });

  const { boundedWidth, boundedHeight } = dimensions;

  // Transform API data to the expected format
  const transformedData = useMemo(() => {
    if (data && data.demographics) {
      const result = {};
      const sentimentOrder = ["positive", "negative", "neutral"];

      // For each demographic value (e.g., "Female", "Male")
      Object.keys(data.demographics).forEach((demographicValue) => {
        // Skip "Prefer not to answer", "skip", and specific ethnicity values
        // Note: "Unknown" is kept for urbanicity and other demographics where it's meaningful
        if (
          demographicValue.toLowerCase().includes("prefer not to answer") ||
          demographicValue.toLowerCase().includes("skip") ||
          demographicValue === "American Indian or Alaska Native" ||
          demographicValue === "Middle Eastern or North African" ||
          demographicValue === "Pacific Islander"
        ) {
          return;
        }

        const sentimentData = data.demographics[demographicValue];

        // Create an object with sentiment as keys and percent as values
        const sentimentPercentages = {};
        let totalPercent = 0;

        // First pass: collect all percentages and calculate total
        sentimentOrder.forEach((sentiment) => {
          if (sentimentData[sentiment] && sentimentData[sentiment].length > 0) {
            sentimentPercentages[sentiment] =
              sentimentData[sentiment][0].unweighted_percent;
            totalPercent += sentimentData[sentiment][0].unweighted_percent;
          } else {
            sentimentPercentages[sentiment] = 0;
          }
        });

        // Second pass: normalize to 100% if total is not 0
        if (totalPercent > 0) {
          sentimentOrder.forEach((sentiment) => {
            sentimentPercentages[sentiment] =
              (sentimentPercentages[sentiment] / totalPercent) * 100;
          });
        }

        result[demographicValue] = sentimentPercentages;
      });

      return result;
    }

    // Fallback to original data format
    return data || {};
  }, [data, data]);

  // Single configuration object for all demographics
  const demographicConfig = useMemo(() => {
    const config = {
      income: {
        labels: {
          less_than_50k: "<$50k",
          "50k_to_100k": "$50k-$100k",
          "100k_plus": "$100k+",
        },
        order: ["less_than_50k", "50k_to_100k", "100k_plus"],
      },
      education: {
        labels: {
          "Up to High School": "Up to High School",
          "High School Graduate": "High School",
          "Some College/Associate's": "Some College",
          "Bachelor's Degree": "Bachelor's",
          "Graduate Degree": "Graduate",
        },
        order: [
          "Up to High School",
          "High School Graduate",
          "Some College/Associate's",
          "Bachelor's Degree",
          "Graduate Degree",
        ],
      },
      ethnicity: {
        labels: {
          White: "White",
          "Black or African American": "Black",
          Hispanic: "Hispanic",
          Asian: "Asian",
          "Some other race": "Other",
          // "American Indian or Alaska Native": "Native American",
          // "Middle Eastern or North African": "Middle Eastern",
          // "Pacific Islander": "Pacific Islander",
        },
        order: [
          "Asian",
          "Black or African American",
          "Hispanic",
          // "Middle Eastern or North African",
          // "American Indian or Alaska Native",
          // "Pacific Islander",
          "White",
          "Some other race",
        ],
      },
      parental_status: {
        labels: {
          parent: "Parent",
          non_parent: "Non-Parent",
        },
        order: ["parent", "non_parent"],
      },
      party: {
        labels: {
          Democrat: "Democrat",
          "Independent lean Dem": "Ind. lean Dem",
          Independent: "Independent",
          "Independent lean Rep": "Ind. lean Rep",
          Republican: "Republican",
        },
        order: [
          "Democrat",
          "Independent lean Dem",
          "Independent",
          "Independent lean Rep",
          "Republican",
        ],
      },
      employment: {
        labels: {
          Employed: "Employed",
          Unemployed: "Unemployed",
          Retired: "Retired",
        },
        order: ["Employed", "Unemployed", "Retired"],
      },
      urbanicity: {
        labels: {
          Urban: "Urban",
          Suburban: "Suburban",
          Rural: "Rural",
        },
        order: ["Urban", "Suburban", "Rural"],
      },
      age_group: {
        labels: {
          "18-24": "18-24",
          "25-34": "25-34",
          "35-44": "35-44",
          "45-64": "45-64",
          "65-84": "65-84",
          "85+": "85+",
        },
        order: ["18-24", "25-34", "35-44", "45-64", "65-84", "85+"],
      },
    };

    return config[demographic] || { labels: {}, order: [] };
  }, [demographic]);

  // Get label mappings and order from config
  const labelMappings = demographicConfig.labels;
  const categoryOrder = demographicConfig.order;

  // Get all demographic categories (y-axis) - these are the demographic values like "Female", "Male"
  const categories = useMemo(() => {
    const categoryKeys = Object.keys(transformedData);

    // Sort age groups in descending order if this is an age_group demographic
    if (
      categoryKeys.length > 0 &&
      categoryKeys.some((key) => key.includes("-") || key === "85+")
    ) {
      const ageGroupOrder = [
        "18-24",
        "25-34",
        "35-44",
        "45-64",
        "65-84",
        "85+",
      ];
      return categoryKeys.sort((a, b) => {
        const aIndex = ageGroupOrder.indexOf(a);
        const bIndex = ageGroupOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    // Use custom ordering if available
    if (categoryOrder.length > 0) {
      return categoryKeys.sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    }

    return categoryKeys;
  }, [transformedData, demographic, categoryOrder]);

  // Get all segments (sentiment groups) - these are "positive", "negative", "neutral"
  const segments = useMemo(() => {
    if (categories.length > 0) {
      return Object.keys(transformedData[categories[0]]);
    }
    return [];
  }, [transformedData, categories]);

  // Create color scale for segments (sentiments)
  const colorScale = useMemo(
    () => (segment, category) => {
      const sentimentIndex = segments.indexOf(segment);
      return colors[sentimentIndex] || colors[0];
    },
    [segments, colors]
  );

  // Create y scale for categories (vertical axis)
  const yScale = useMemo(
    () =>
      scaleBand({
        domain: categories,
        range: [0, boundedHeight],
        padding: 0.4,
      }),
    [categories, boundedHeight]
  );

  // Create x scale for values (horizontal axis)
  const xScale = useMemo(() => {
    // Use fixed maximum of 100 to ensure all bars have the same width
    return scaleLinear({
      domain: [0, 100],
      range: [0, boundedWidth],
      nice: true,
    });
  }, [boundedWidth]);

  // Calculate the stacked bar data
  const stackedData = useMemo(() => {
    return categories.map((category) => {
      let xOffset = 0;
      const segmentData = Object.keys(transformedData[category]).map(
        (segment) => {
          const value = transformedData[category][segment] || 0;
          const barWidth = xScale(value);
          const startX = xOffset;
          xOffset += barWidth;
          return {
            segment,
            value,
            startX,
            width: barWidth,
          };
        }
      );
      return {
        category,
        segments: segmentData,
        total: segmentData.reduce((sum, d) => sum + d.value, 0),
      };
    });
  }, [transformedData, categories, xScale]);

  // Format percentage for display
  const formatPercentage = (value, total) => {
    if (total === 0) return "0%";
    const percentage = (value / total) * 100;
    return `${percentage.toFixed(0)}%`;
  };

  // Format sentiment label
  const formatSentimentLabel = (sentiment) => {
    return sentiment.charAt(0).toUpperCase() + sentiment.slice(1);
  };

  // Calculate dynamic font size based on label length and available space
  const calculateFontSize = (
    label,
    maxWidth,
    maxFontSize = 16,
    minFontSize = 10
  ) => {
    // Estimate character width (approximate for most fonts)
    const avgCharWidth = 0.6; // Adjust this based on your font
    const estimatedWidth = label.length * avgCharWidth * maxFontSize;

    if (estimatedWidth <= maxWidth) {
      return maxFontSize;
    }

    // Calculate the font size that would fit the text
    const calculatedSize = maxWidth / (label.length * avgCharWidth);
    return Math.max(minFontSize, Math.min(maxFontSize, calculatedSize));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 pl-4">
        <div className="text-lg font-semibold text-gray-600 flex items-center">
          Loading
          <span className="text-mblue-700 ml-1 animate-pulse">...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg font-semibold text-red-600">
          Error loading data: {error}
        </div>
      </div>
    );
  }

  // Show empty state
  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg font-semibold text-gray-600">
          No demographic data available
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full overflow-x-auto">
        <div
          ref={containerRef}
          className={`demographic-stacked-bar-chart ${className}`}
          style={{ position: "relative", minWidth: width }}
        >
          <svg
            width={width}
            height={height}
            role="img"
            aria-labelledby="chart-desc"
          >
            {description && <desc id="chart-desc">{description}</desc>}
            <Group top={margin.top} left={margin.left}>
              {/* Render bars */}
              {stackedData.map(
                ({ category, segments: categorySegments, total }) => {
                  const y = yScale(category) || 0;
                  const barHeight = yScale.bandwidth();
                  return (
                    <Group key={category}>
                      {/* Render bars */}
                      {categorySegments.map(({ segment, startX, width }) => (
                        <Bar
                          key={`${category}-${segment}`}
                          x={startX}
                          y={y}
                          width={width}
                          height={barHeight}
                          fill={colorScale(segment, category)}
                        />
                      ))}

                      {/* Show percentages in the bars */}
                      {showPercentages &&
                        categorySegments.map(
                          ({ segment, startX, width, value }) => {
                            if (width < 40) return null;
                            return (
                              <Text
                                key={`text-${category}-${segment}`}
                                x={startX + width / 2}
                                y={y + barHeight / 2}
                                textAnchor="middle"
                                verticalAnchor="middle"
                                fill="#ffffff"
                                fontSize={12}
                                fontWeight={600}
                              >
                                {formatPercentage(value, total)}
                              </Text>
                            );
                          }
                        )}
                    </Group>
                  );
                }
              )}
              {/* Y Axis (categories) */}
              <AxisLeft
                scale={yScale}
                stroke="transparent"
                tickLength={0}
                hideTicks={true}
                tickComponent={({ x, y, formattedValue }) => {
                  // Use custom label mapping if available
                  const displayLabel =
                    labelMappings[formattedValue] || formattedValue;

                  // Calculate dynamic font size
                  const availableWidth = margin.left - 20;
                  const fontSize = calculateFontSize(
                    displayLabel,
                    availableWidth,
                    16,
                    10
                  );

                  return (
                    <Text
                      x={x - 10}
                      y={y}
                      width={availableWidth}
                      textAnchor="end"
                      verticalAnchor="middle"
                      fill="#333333"
                      fontSize={fontSize}
                      fontFamily="Overpass"
                      fontWeight={300}
                      lineHeight={"1.3em"}
                    >
                      {displayLabel}
                    </Text>
                  );
                }}
              />
            </Group>
          </svg>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="flex gap-6 mt-8 justify-start">
            {segments.map((segment, index) => (
              <span
                key={segment}
                className="flex items-center font-semibold text-base"
              >
                <span
                  className="w-5 h-5 mr-2 inline-block rounded-full border-2 border-gray-200"
                  style={{ background: colors[index] }}
                ></span>
                {formatSentimentLabel(segment)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Wrapper component that handles API calls
// Cache for storing API responses to avoid repeated calls
const demographicDataCache = new Map();
const MAX_CACHE_SIZE = 100; // Maximum number of cached entries
const CACHE_EXPIRY_TIME = 30 * 60 * 1000; // 30 minutes in milliseconds

// Helper function to generate cache key
const getCacheKey = (term, demographic, ageGroup) => {
  return `${term}|${demographic}|${ageGroup || ""}`;
};

// Helper function to clean up old cache entries
const cleanupCache = () => {
  const now = Date.now();
  const entriesToDelete = [];

  // Find entries that are expired or if cache is too large
  for (const [key, value] of demographicDataCache.entries()) {
    if (now - value.timestamp > CACHE_EXPIRY_TIME) {
      entriesToDelete.push(key);
    }
  }

  // If cache is still too large after removing expired entries, remove oldest entries
  if (demographicDataCache.size - entriesToDelete.length > MAX_CACHE_SIZE) {
    const sortedEntries = Array.from(demographicDataCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp
    );

    const additionalToDelete =
      demographicDataCache.size - entriesToDelete.length - MAX_CACHE_SIZE;
    for (let i = 0; i < additionalToDelete; i++) {
      entriesToDelete.push(sortedEntries[i][0]);
    }
  }

  // Delete the identified entries
  entriesToDelete.forEach((key) => demographicDataCache.delete(key));
};

// Helper function to add entry to cache with cleanup
const addToCache = (key, data, error) => {
  // Clean up cache before adding new entry
  cleanupCache();

  demographicDataCache.set(key, {
    data,
    error,
    timestamp: Date.now(),
  });
};

// Utility function to manually clear cache (useful for debugging or force refresh)
export const clearDemographicCache = () => {
  demographicDataCache.clear();
  console.log("Demographic data cache cleared");
};

// Utility function to get cache statistics
export const getDemographicCacheStats = () => {
  return {
    size: demographicDataCache.size,
    maxSize: MAX_CACHE_SIZE,
    expiryTime: CACHE_EXPIRY_TIME,
  };
};

export const DemographicStackedBarChartWithAPI = ({
  term,
  demographic = "gender",
  ageGroup,
  ...props
}) => {
  const [data, setdata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Debounce the term to prevent excessive API calls
  const debouncedTerm = useDebounce(term, 500); // 500ms delay

  useEffect(() => {
    if (!debouncedTerm) {
      setdata(null);
      setError(null);
      return;
    }

    const fetchDemographicData = async () => {
      const cacheKey = getCacheKey(debouncedTerm, demographic, ageGroup);

      // Check if data is already cached
      if (demographicDataCache.has(cacheKey)) {
        const cachedData = demographicDataCache.get(cacheKey);
        console.log(`Cache HIT for demographic data: ${cacheKey}`);
        setdata(cachedData.data);
        setError(cachedData.error);
        setLoading(false);
        return;
      }

      console.log(`Cache MISS for demographic data: ${cacheKey}`);
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          term: debouncedTerm,
          demographic: demographic,
        });

        if (ageGroup) {
          params.append("age_group", ageGroup);
        }

        const response = await apiClient.get(
          `/api/civic-pulse/search-demographics?${params}`
        );

        // Cache the successful response
        addToCache(cacheKey, response.data, null);

        setdata(response.data);
      } catch (err) {
        console.error("Error fetching demographic data:", err);
        const errorMessage =
          err.response?.data?.detail || "Failed to load demographic data";

        // Cache the error response as well to avoid repeated failed requests
        addToCache(cacheKey, null, errorMessage);

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchDemographicData();
  }, [debouncedTerm, demographic, ageGroup]);

  return (
    <DemographicStackedBarChart
      data={data}
      loading={loading}
      error={error}
      demographic={demographic}
      {...props}
    />
  );
};

export default DemographicStackedBarChart;
